import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveComponent as _resolveComponent, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { CableVessel, MapCableDataPoint } from '@/server/MakeSenseApp-backend'
import { ref, PropType, inject, computed } from 'vue'
import { usePopupStore } from '@/stores/PopupStore'
import { degreesToRadians, getVesselIcon } from '@/utilities/vesselMappings'

const strokeColor = '#00215F'
const POPUP_OFFSET_X = -320
const POPUP_WIDTH    = 380
const POPUP_H_CABLE  = 175
const POPUP_H_VESSEL = 240
const MARGIN         = 16   // minimum clear gap between popup and any obstacle

interface Rect { left: number; top: number; right: number; bottom: number }

/** Convert a DOMRect to our plain Rect type */

export default /*@__PURE__*/_defineComponent({
  __name: 'InteractionSelect',
  props: {
  sensorReadings: {
    type: Array as PropType<MapCableDataPoint[]>,
    required: true
  },
  vessels: {
    type: Array as PropType<CableVessel[]>,
    required: true
  }
},
  setup(__props) {

const popupStore = usePopupStore()
const strokeWidth = ref(12)
const selectedVessel = computed(() => popupStore.vesselData)

const props = __props

const selectConditions = inject('ol-selectconditions')
const selectCondition = selectConditions.click
const mapRef = inject<any>('map')

// PopupMap offset=[-320, -popupHeight] positions the OL overlay element so:
//   popup top-left = (anchorX - 320, anchorY - popupHeight)
//   popup bottom-right = (anchorX + 60, anchorY)
function toRect(d: DOMRect): Rect {
  return { left: d.left, top: d.top, right: d.right, bottom: d.bottom }
}

/**
 * Calculate popup position in viewport coordinates.
 */
function popupRectFromAnchor(
  anchorCanvasPx: [number, number],
  popupHeight: number,
  mapRect: Rect
): Rect {
  const ax = mapRect.left + anchorCanvasPx[0]  // canvas px → viewport px
  const ay = mapRect.top  + anchorCanvasPx[1]
  return {
    left:   ax + POPUP_OFFSET_X,
    top:    ay - popupHeight,
    right:  ax + POPUP_OFFSET_X + POPUP_WIDTH,
    bottom: ay,
  }
}

/** Check if two rects overlap */
function overlaps(a: Rect, b: Rect): boolean {
  return !(
    a.right  <= b.left   + MARGIN ||
    a.left   >= b.right  - MARGIN ||
    a.bottom <= b.top    + MARGIN ||
    a.top    >= b.bottom - MARGIN
  )
}

/**
 * Collect DOM elements that popup must avoid (main panel, zoom control, etc).
 */
function getObstacles(): Rect[] {
  const selectors = ['main', '.ol-zoom', '[data-map-overlay]']
  const rects: Rect[] = []
  for (const sel of selectors) {
    document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
      const r = el.getBoundingClientRect()
      if (r.width > 0 && r.height > 0) rects.push(toRect(r))
    })
  }
  return rects
}

/**
 * Check if popup collides with obstacles or map boundaries.
 */
function hasCollision(popupRect: Rect, mapRect: Rect, obstacles: Rect[]): boolean {
  // 1. Check map-canvas boundaries (popup must stay fully inside the map)
  const mapBounds: Rect = {
    left:   mapRect.left   + MARGIN,
    top:    mapRect.top    + MARGIN,
    right:  mapRect.right  - MARGIN,
    bottom: mapRect.bottom - MARGIN,
  }
  if (
    popupRect.left   < mapBounds.left   ||
    popupRect.top    < mapBounds.top    ||
    popupRect.right  > mapBounds.right  ||
    popupRect.bottom > mapBounds.bottom
  ) {
    return true
  }

  // 2. Check overlay obstacles
  for (const obs of obstacles) {
    if (overlaps(popupRect, obs)) return true
  }

  return false
}

/**
 * Find best viewport anchor position to center popup in largest clear area.
 */
function idealViewportAnchor(
  mapRect: Rect,
  obstacles: Rect[],
  popupHeight: number
): [number, number] {
  // Start with the full map canvas as the safe zone
  let safeLeft   = mapRect.left   + MARGIN
  let safeTop    = mapRect.top    + MARGIN
  let safeRight  = mapRect.right  - MARGIN
  let safeBottom = mapRect.bottom - MARGIN

  // Each obstacle that overlaps the map canvas can shrink one side of the safe zone
  for (const obs of obstacles) {
    // Skip obstacles that don't actually overlap the map canvas at all
    if (obs.right <= mapRect.left || obs.left >= mapRect.right ||
        obs.bottom <= mapRect.top || obs.top >= mapRect.bottom) continue

    // Which side of the map does this obstacle intrude from?
    const fromLeft   = obs.right  - mapRect.left
    const fromRight  = mapRect.right  - obs.left
    const fromTop    = obs.bottom - mapRect.top
    const fromBottom = mapRect.bottom - obs.top

    const minIntrusion = Math.min(fromLeft, fromRight, fromTop, fromBottom)

    if (minIntrusion === fromLeft)   safeLeft   = Math.max(safeLeft,   obs.right  + MARGIN)
    if (minIntrusion === fromRight)  safeRight  = Math.min(safeRight,  obs.left   - MARGIN)
    if (minIntrusion === fromTop)    safeTop    = Math.max(safeTop,    obs.bottom + MARGIN)
    if (minIntrusion === fromBottom) safeBottom = Math.min(safeBottom, obs.top    - MARGIN)
  }

  // Centre of the safe zone in viewport px
  const cx = (safeLeft + safeRight)  / 2
  const cy = (safeTop  + safeBottom) / 2

  // Reverse popup-offset math to get anchor from desired popup-centre:
  //   popup centre X = ax + POPUP_OFFSET_X + POPUP_WIDTH / 2  → ax = cx - POPUP_OFFSET_X - W/2
  //   popup centre Y = ay - popupHeight / 2                   → ay = cy + H/2
  const ax = cx - POPUP_OFFSET_X - POPUP_WIDTH / 2
  const ay = cy + popupHeight / 2

  return [ax, ay]
}

/**
 * Pan map so clickCoord appears at targetViewportPx.
 */
function panTo(olMap: any, clickCoord: number[], targetViewportPx: [number, number]) {
  const mapEl: HTMLElement   = olMap.getTargetElement()
  const mapRect              = mapEl.getBoundingClientRect()

  // Convert viewport px → canvas px for getPixelFromCoordinate
  const targetCanvasPx: [number, number] = [
    targetViewportPx[0] - mapRect.left,
    targetViewportPx[1] - mapRect.top,
  ]

  const view   = olMap.getView()
  const res: number              = view.getResolution()
  const center: [number, number] = view.getCenter()
  const curPx:  [number, number] = olMap.getPixelFromCoordinate(clickCoord)

  if (!curPx) return

  const dx = targetCanvasPx[0] - curPx[0]
  const dy = targetCanvasPx[1] - curPx[1]

  if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return

  view.animate({
    center: [center[0] - dx * res, center[1] + dy * res],
    duration: 380,
  })
}

function avoidOverlapIfNeeded(
  clickCoord: number[],
  clickCanvasPx: [number, number],
  popupHeight: number
) {
  const olMap = mapRef?.map ?? mapRef?.value ?? mapRef
  if (!olMap || typeof olMap.getView !== 'function') return

  const mapEl: HTMLElement = olMap.getTargetElement()
  if (!mapEl) return

  const mapRect  = toRect(mapEl.getBoundingClientRect())
  const obstacles = getObstacles()

  // Where will the popup appear with the current click position?
  const pRect = popupRectFromAnchor(clickCanvasPx, popupHeight, mapRect)

  // Only pan if there's actually a problem
  if (!hasCollision(pRect, mapRect, obstacles)) return

  // Compute where the anchor should be so popup sits centred in the safe area
  const [targetVpX, targetVpY] = idealViewportAnchor(mapRect, obstacles, popupHeight)

  panTo(olMap, clickCoord, [targetVpX, targetVpY])
}

const featureSelected = (event: any) => {
  const selected                        = event.selected
  const clickCoord: number[]            = event.mapBrowserEvent.coordinate
  const clickCanvasPx: [number, number] = event.mapBrowserEvent.pixel

  if (selected.length > 0) {
    if (selected[0].values_.type === 'cable') {
      const idx: number = selected[0].values_.name
      popupStore.showCabelData(props.sensorReadings[idx], clickCoord[0], clickCoord[1])
      avoidOverlapIfNeeded(clickCoord, clickCanvasPx, POPUP_H_CABLE)
    }

    if (selected[0].values_.type === 'vessel') {
      const mmsi: number = selected[0].values_.name
      const vessel = props.vessels.find((x) => x.mmsi === mmsi)
      if (vessel) {
        popupStore.showVesselData(vessel, clickCoord[0], clickCoord[1])
        avoidOverlapIfNeeded(clickCoord, clickCanvasPx, POPUP_H_VESSEL)
      }
    }
  } else {
    popupStore.closePopup()
  }
}

const selectInteractionFilter = (feature: any) => {
  return feature.values_.type != undefined
}

return (_ctx: any,_cache: any) => {
  const _component_ol_style_stroke = _resolveComponent("ol-style-stroke")!
  const _component_ol_style_icon = _resolveComponent("ol-style-icon")!
  const _component_ol_style = _resolveComponent("ol-style")!
  const _component_ol_interaction_select = _resolveComponent("ol-interaction-select")!

  return (_openBlock(), _createBlock(_component_ol_interaction_select, {
    onSelect: featureSelected,
    condition: _unref(selectCondition),
    filter: selectInteractionFilter
  }, {
    default: _withCtx(() => [
      _createVNode(_component_ol_style, null, {
        default: _withCtx(() => [
          _createVNode(_component_ol_style_stroke, {
            color: strokeColor,
            width: strokeWidth.value
          }, null, 8, ["width"]),
          _createVNode(_component_ol_style_icon, {
            src: _unref(getVesselIcon)(selectedVessel.value),
            width: 18,
            height: 20,
            rotateWithView: true,
            rotation: _unref(degreesToRadians)(selectedVessel.value?.angle ?? 0)
          }, null, 8, ["src", "rotation"])
        ]),
        _: 1
      })
    ]),
    _: 1
  }, 8, ["condition"]))
}
}

})